import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { fetchCountries } from "@/lib/api";
import AtlasMap from "@/components/AtlasMap";
import CountryPanel from "@/components/CountryPanel";
import AlphabetMenu from "@/components/AlphabetMenu";
import FlagGrid from "@/components/FlagGrid";
import SearchBar from "@/components/SearchBar";
import AdSlot from "@/components/AdSlot";
import TopRated from "@/components/TopRated";
import TopMedia from "@/components/TopMedia";
import LatestReviews from "@/components/LatestReviews";
import HomeTiles from "@/components/HomeTiles";
import CountryWheel from "@/components/CountryWheel";
import { usePassport } from "@/lib/usePassport";
import { recordVisit } from "@/lib/engagement";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { Shuffle, Clock, Star, Globe2, ArrowRight, BookOpen } from "lucide-react";
import { useFavorites } from "@/lib/useFavorites";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";

const RISE = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

export default function Home() {
  const { t, lang, isRtl } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCode = searchParams.get("c");
  const setSelectedCode = useCallback((code) => {
    if (code) setSearchParams({ c: code }, { replace: false });
    else setSearchParams({}, { replace: false });
  }, [setSearchParams]);
  const [recent, setRecent] = useState(() => storage.getJSON("atlas.recent", []));
  const trackedCode = useRef(null);

  const { favorites } = useFavorites();
  const { stamp } = usePassport();
  useEffect(() => { recordVisit(); }, []);
  const { data: countries = [], isLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
  });

  const selected = useMemo(
    () => countries.find((c) => c.code === selectedCode) || null,
    [countries, selectedCode],
  );

  // Track recently viewed countries (top 5, most recent first)
  useEffect(() => {
    if (!selectedCode || trackedCode.current === selectedCode) return;
    trackedCode.current = selectedCode;
    trackEvent("select_country", { country_code: selectedCode, lang: document.documentElement.lang });
    const { isNew, count, badge } = stamp(selectedCode);
    if (isNew) {
      const name = countries.find((c) => c.code === selectedCode)?.short_names[lang] || selectedCode;
      toast(t("stamp_new").replace("{country}", name), { description: `${count}/${countries.length}${badge ? ` · ${t("badge_unlocked")} ${t(`badge_${badge.id}`)}` : ""}`, id: "stamp" });
    }
    setRecent((prev) => {
      const next = [selectedCode, ...prev.filter((c) => c !== selectedCode)].slice(0, 6);
      storage.setJSON("atlas.recent", next);
      return next;
    });
  }, [selectedCode, stamp, countries, lang, t]);

  const pickRandom = () => {
    if (countries.length === 0) return;
    let code;
    do {
      code = countries[Math.floor(Math.random() * countries.length)].code;
    } while (code === selectedCode && countries.length > 1);
    setSearchParams({ c: code }, { replace: true });
  };

  const sortedCodes = useMemo(() => {
    const key = lang === "ar" ? "ar" : "en";
    return [...countries]
      .sort((a, b) => (a.short_names[key] || a.short_names.en).localeCompare(b.short_names[key] || b.short_names.en, key))
      .map((c) => c.code);
  }, [countries, lang]);

  // Materialise the "recently viewed" strip once per data change instead of
  // rebuilding on every render.
  const recentCountries = useMemo(
    () => recent.map((code) => countries.find((c) => c.code === code)).filter(Boolean),
    [recent, countries],
  );

  const favoriteCountries = useMemo(
    () => favorites.map((code) => countries.find((c) => c.code === code)).filter(Boolean),
    [favorites, countries],
  );

  // Preload every flag once so the panel + minimap open instantly.
  useEffect(() => {
    countries.forEach((c) => { const img = new Image(); img.src = c.flag_url; });
  }, [countries]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "Escape") { setSelectedCode(null); return; }
      if (e.key === "/") {
        e.preventDefault();
        document.querySelector('[data-testid="search-input"]')?.focus();
        return;
      }
      if (!selectedCode || sortedCodes.length === 0) return;
      const i = sortedCodes.indexOf(selectedCode);
      const forward = (isRtl ? "ArrowLeft" : "ArrowRight");
      const backward = (isRtl ? "ArrowRight" : "ArrowLeft");
      if (e.key === forward) setSelectedCode(sortedCodes[(i + 1) % sortedCodes.length]);
      else if (e.key === backward) setSelectedCode(sortedCodes[(i - 1 + sortedCodes.length) % sortedCodes.length]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedCode, sortedCodes, isRtl, setSelectedCode]);

  return (
    <div className="min-h-[calc(100vh-73px)] mx-auto max-w-7xl px-3 sm:px-8 py-3 sm:py-10">
      {/* Hero row — title hidden on phones (already in the sticky header) so the map is visible immediately */}
      <div className="mb-4 sm:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
        <div className="hidden sm:block">
          <motion.h1
            {...RISE}
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-ink leading-none tracking-tight"
            data-testid="home-title"
          >
            {t("app_title")}
          </motion.h1>
          <p className="mt-3 text-base text-muted-foreground max-w-xl">{t("app_tagline")}</p>
        </div>
        <div className="w-full md:w-96">
          <SearchBar countries={countries} onSelect={setSelectedCode} />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground/80 hidden md:block" data-testid="kbd-hint">
              {t("kbd_hint")}
            </p>
            <button
              onClick={pickRandom}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stoneLine hover:bg-sandDeep/50 transition-colors ms-auto"
              data-testid="random-button"
            >
              <Shuffle className="w-3.5 h-3.5" /> {t("random")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Map */}
        <div className="lg:col-span-8 rounded-2xl sm:rounded-3xl border border-stoneLine bg-card p-1.5 sm:p-5 relative overflow-hidden">
          {isLoading ? (
            <div className="aspect-[820/550] w-full flex items-center justify-center text-muted-foreground">…</div>
          ) : (
            <AtlasMap countries={countries} onSelect={setSelectedCode} selectedCode={selectedCode} />
          )}
        </div>

        {/* Alphabet menu */}
        <div className="lg:col-span-4 hidden lg:block">
          <AlphabetMenu countries={countries} onSelect={setSelectedCode} selectedCode={selectedCode} />
        </div>
      </div>

      {countries.length > 0 && <div className="mt-4 sm:mt-6"><CountryWheel countries={countries} onSelect={setSelectedCode} /></div>}
      {countries.length > 0 && <HomeTiles countries={countries} onSelect={setSelectedCode} />}
      <div className="lg:hidden mt-4"><FlagGrid countries={countries} onSelect={setSelectedCode} selectedCode={selectedCode} /></div>

      {/* Favourites */}
      {favoriteCountries.length > 0 && (
        <div className="mt-6" data-testid="favorites-strip">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Star className="w-3 h-3 text-ochre" fill="currentColor" /> {t("favorites")}
          </div>
          <div className="flex gap-2 overflow-x-auto thin-scroll pb-2">
            {favoriteCountries.map((c) => (
              <button
                key={c.code}
                onClick={() => setSelectedCode(c.code)}
                className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                  selectedCode === c.code
                    ? "bg-ochre/10 border-ochre text-ochre"
                    : "border-ochre/30 bg-ochre/5 hover:bg-ochre/10 text-ink"
                }`}
                data-testid={`favorite-${c.code}`}
              >
                <img src={c.flag_url} alt="" className="w-5 h-3.5 object-cover rounded-sm border border-stoneLine" />
                <span className="text-sm">{c.short_names[lang]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Community */}
      <div id="community" className="scroll-mt-20" />
      {countries.length > 0 && <TopRated countries={countries} selectedCode={selectedCode} onSelect={setSelectedCode} />}
      {countries.length > 0 && <TopMedia countries={countries} onSelect={setSelectedCode} />}
      {countries.length > 0 && <LatestReviews countries={countries} onSelect={setSelectedCode} />}

      {/* Recently viewed */}
      {recent.length > 1 && countries.length > 0 && (
        <div className="mt-6" data-testid="recent-strip">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Clock className="w-3 h-3" /> {t("recent")}
          </div>
          <div className="flex gap-2 overflow-x-auto thin-scroll pb-2">
            {recentCountries.map((c) => (
              <button
                key={c.code}
                onClick={() => setSelectedCode(c.code)}
                className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                  selectedCode === c.code
                    ? "bg-ochre/10 border-ochre text-ochre"
                    : "border-stoneLine hover:bg-sandDeep/50 text-ink"
                }`}
                data-testid={`recent-${c.code}`}
              >
                <img src={c.flag_url} alt="" className="w-5 h-3.5 object-cover rounded-sm border border-stoneLine" />
                <span className="text-sm">{c.short_names[lang]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <CountryPanel
        country={selected}
        countries={countries}
        onClose={() => setSelectedCode(null)}
        onNavigate={(code) => setSelectedCode(code)}
      />

      <div className="mt-12 max-w-3xl mx-auto">
        <AdSlot placement="footer" />
      </div>

      <footer className="mt-10 text-center text-xs text-muted-foreground">
        {t("footer")}
        <span className="mx-2">·</span>
        <Link to="/overview" className="text-ochre hover:underline" data-testid="footer-overview-link">{t("overview_kicker")}</Link>
        <span className="mx-2">·</span>
        <Link to="/ricettario" className="text-ochre hover:underline" data-testid="footer-cookbook-link">{t("cookbook")}</Link>
        <span className="mx-2">·</span>
        <Link to="/ebook" className="text-ochre hover:underline" data-testid="footer-ebook-link">{t("ebook")}</Link>
        <span className="mx-2">·</span>
        <Link to="/privacy" className="text-ochre hover:underline" data-testid="footer-privacy-link">{t("privacy")}</Link>
        <span className="mx-2">·</span>
        <Link to="/regole" className="text-ochre hover:underline" data-testid="footer-rules-link">{t("rules_title")}</Link>
      </footer>
    </div>
  );
}
